import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

	pixPaper: {
		padding: theme.spacing(2),
		marginBottom: 12,
	},
	pixHeader: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginBottom: 12,
	},
	pixIcon: {
		width: 32,
		height: 32,
		borderRadius: 8,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "rgba(5, 150, 105, 0.1)",
		color: theme.palette.primary.main,
	},
	pixTitle: {
		fontWeight: 700,
	},
	pixActions: {
		display: "flex",
		justifyContent: "flex-end",
		marginTop: 12,
	},
}));

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);
	const [pixConfig, setPixConfig] = useState({
		pixKeyType: "cpf",
		pixKey: "",
		pixName: "",
		pixCity: "",
	});
	const [savingPix, setSavingPix] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		const fetchPixConfig = async () => {
			try {
				const { data } = await api.get("/pix-config");
				setPixConfig({
					pixKeyType: data.pixKeyType || "cpf",
					pixKey: data.pixKey || "",
					pixName: data.pixName || "",
					pixCity: data.pixCity || "",
				});
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
		fetchPixConfig();
	}, []);

	const handleChangePixField = e => {
		const { name, value } = e.target;
		setPixConfig(prev => ({ ...prev, [name]: value }));
	};

	const handleSavePix = async () => {
		setSavingPix(true);
		try {
			await api.put("/pix-config", pixConfig);
			toast.success(i18n.t("settings.pix.saved"));
		} catch (err) {
			toastError(err);
		}
		setSavingPix(false);
	};

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const { value } = settings.find(s => s.key === key);
		return value;
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.pixPaper}>
					<div className={classes.pixHeader}>
						<div className={classes.pixIcon}>
							<AttachMoneyIcon fontSize="small" />
						</div>
						<div>
							<Typography variant="body1" className={classes.pixTitle}>
								{i18n.t("settings.pix.title")}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								{i18n.t("settings.pix.subtitle")}
							</Typography>
						</div>
					</div>
					<Grid container spacing={1}>
						<Grid item xs={12} sm={4}>
							<Select
								margin="dense"
								variant="outlined"
								native
								fullWidth
								name="pixKeyType"
								value={pixConfig.pixKeyType}
								onChange={handleChangePixField}
							>
								<option value="cpf">CPF</option>
								<option value="cnpj">CNPJ</option>
								<option value="phone">
									{i18n.t("settings.pix.keyTypes.phone")}
								</option>
								<option value="email">E-mail</option>
								<option value="random">
									{i18n.t("settings.pix.keyTypes.random")}
								</option>
							</Select>
						</Grid>
						<Grid item xs={12} sm={8}>
							<TextField
								label={i18n.t("settings.pix.key")}
								name="pixKey"
								value={pixConfig.pixKey}
								onChange={handleChangePixField}
								margin="dense"
								variant="outlined"
								fullWidth
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label={i18n.t("settings.pix.name")}
								helperText={i18n.t("settings.pix.nameHelper")}
								name="pixName"
								value={pixConfig.pixName}
								onChange={handleChangePixField}
								margin="dense"
								variant="outlined"
								fullWidth
							/>
						</Grid>
					</Grid>
					<div className={classes.pixActions}>
						<Button
							variant="contained"
							color="primary"
							disabled={savingPix}
							onClick={handleSavePix}
						>
							{i18n.t("settings.pix.save")}
						</Button>
					</div>
				</Paper>

			</Container>
		</div>
	);
};

export default Settings;
